import { passwordSchema } from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { changePassword } from './api.js';

const formSchema = z
  .object({
    currentPassword: z.string().min(1, 'Mot de passe actuel requis'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Confirmation requise'),
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['confirmPassword'],
  });

type ChangePasswordValues = z.infer<typeof formSchema>;

export interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Menu de compte — `02` §13 : « chaque utilisateur peut changer son mot de passe depuis le menu de compte ». */
export function ChangePasswordDialog({
  open,
  onOpenChange,
}: ChangePasswordDialogProps): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordValues>({ resolver: zodResolver(formSchema) });

  const mutation = useMutation({
    mutationFn: (values: ChangePasswordValues) =>
      changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword }),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Mot de passe mis à jour.');
      reset();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      setError('root', {
        message: error instanceof ApiError ? error.message : 'Une erreur technique est survenue.',
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Changer le mot de passe</DialogTitle>
          <DialogDescription>
            Le nouveau mot de passe doit contenir au moins 12 caractères.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(event) => {
            void handleSubmit((values) => {
              mutation.mutate(values);
            })(event);
          }}
        >
          <Field
            label="Mot de passe actuel"
            htmlFor="currentPassword"
            error={errors.currentPassword?.message}
          >
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              aria-invalid={errors.currentPassword ? true : undefined}
              {...register('currentPassword')}
            />
          </Field>
          <Field
            label="Nouveau mot de passe"
            htmlFor="newPassword"
            error={errors.newPassword?.message}
          >
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.newPassword ? true : undefined}
              {...register('newPassword')}
            />
          </Field>
          <Field
            label="Confirmation"
            htmlFor="confirmPassword"
            error={errors.confirmPassword?.message}
          >
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.confirmPassword ? true : undefined}
              {...register('confirmPassword')}
            />
          </Field>
          {errors.root ? (
            <p
              role="alert"
              className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg"
            >
              {errors.root.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="submit" isLoading={isSubmitting || mutation.isPending}>
              Mettre à jour
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
